import { useEffect, useState } from "react";
import { Box, Text } from "@vinomofo/components";
import { ProductPreloader } from "../components/preloaders/ProductPreloader";

import Helmet from "react-helmet";
import { Layout } from "../components/layout";
import { OfferDetails } from "../components/offer-details";
import { OfferPageProps } from "../types/vino-data-types";
import SEO from "../components/seo/seo";
import config from "../../config.json";
import isEqual from "lodash.isequal";
import mapSlugGroup from "../utils/map-slug-group";
import vinoFetch from "../utils/vinoFetch";

const OpenOfferPage = ({ pageContext }: OfferPageProps): JSX.Element => {
  const { offer } = pageContext;
  const [offerDetails, setOfferDetails] = useState(offer);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const seoSubTitle = offerDetails?.isMixed
    ? " - Mixed Case"
    : offerDetails?.category &&
      offerDetails?.category.toLocaleLowerCase() !== "sparkling"
    ? ` - ${offerDetails?.category} Wine`
    : offerDetails?.category
    ? ` - ${offerDetails?.category} `
    : "";

  const isLoyaltyOffer =
    offerDetails?.tiersEligible?.length > 0 &&
    !offerDetails?.tiersEligible.some((tier) =>
      ["GUEST", "ALL"].includes(tier)
    );

  useEffect(() => {
    vinoFetch
      .get(`/api/refresh-offer?id=${offerDetails.id}`)
      .then((res) => {
        setRefresh(true);
        if (!isEqual(res.data.offer, offerDetails)) {
          setOfferDetails(res.data.offer);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [offer]);

  return (
    offerDetails && (
      <Layout pathname={offerDetails?.route?.path}>
        <SEO
          title={`${offerDetails.title || offerDetails.name}${seoSubTitle}`}
          image={offerDetails.cardImage?.url || ""}
          type={"product"}
          description={offerDetails.shortDescription}
          pathname={offerDetails.route?.path || ""}
          robots={
            isLoyaltyOffer
              ? "noindex, nofollow"
              : offerDetails.noIndex
              ? "noindex, nofollow"
              : "index, follow"
          }
        />
        {!isLoyaltyOffer && (
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org/",
                "@type": "Product",
                name: offerDetails.title || offerDetails.name,
                image: offerDetails.cardImage?.url || "",
                ...(process.env.GATSBY_VM_MARKET === "AU" && {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: 4.9,
                    ratingCount: 376,
                  },
                }),
                offers: {
                  "@type": "Offer",
                  url: `${process.env.GATSBY_APP_URL}/${
                    offerDetails.isSpirit
                      ? `spirits/${offerDetails?.category?.toLowerCase()}`
                      : `wines${mapSlugGroup(offerDetails)}`
                  }/${offerDetails.route?.slug || ""}`,
                  priceCurrency:
                    config[process.env.GATSBY_VM_MARKET]?.VM_CURRENCY_CODE ||
                    "AUD",
                  price: offerDetails?.displayPrice || 0,
                  itemCondition: "https://schema.org/NewCondition",
                  availability: "https://schema.org/InStock",
                },
              })}
            </script>
          </Helmet>
        )}

        {isLoyaltyOffer ? (
          <Box minHeight="20vh">
            {loading ? (
              <ProductPreloader />
            ) : error ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={[4, 5]}
                px={[3, 4]}
              >
                <Text variant="sm" textAlign="center">
                  {error}
                </Text>
              </Box>
            ) : (
              <OfferDetails offer={offerDetails} refresh={refresh} />
            )}
          </Box>
        ) : (
          <OfferDetails offer={offerDetails} refresh={refresh} />
        )}
      </Layout>
    )
  );
};

export default OpenOfferPage;
